import './LoginPage.scss';
import { useState, useContext } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Logo from '../../assets/logo-chatbot.png';
import LoginForm from '../../components/LoginForm/LoginForm';
import TwoFactorSetup from '../../components/TwoFactor/TwoFactorSetup';
import TwoFactorVerify from '../../components/TwoFactor/TwoFactorVerify';

export default function LoginPage() {
  const { setToken, axiosAuth, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  // 'login' -> credenciais | 'mfa_setup' -> 1ª vez, 2FA obrigatório | 'mfa_verify' ->
  // 2FA já ativo, falta o código (ver Token em app/api/v1/auth.py)
  const [step, setStep] = useState('login');
  const [mfaToken, setMfaToken] = useState('');

  const todayDate = new Date();
  const year = todayDate.getFullYear();

  function finishLogin(token) {
    if (!token) throw new Error('Token ausente na resposta');
    // guardar token + configurar axios para as próximas chamadas
    setToken(token);
    refreshUser().then(() => {
      // navegar para a raiz: o RoleBasedRedirect encaminha para o Dashboard
      // correto conforme o perfil (superadmin -> /dashboard, cliente -> /my-dashboard)
      navigate('/', { replace: true });
    });
  }

  async function handleLogin({ username, password }) {
    setError(null);
    setLoading(true);
    try {
      const form = new URLSearchParams({ username, password });
      const resp = await axiosAuth.post('/auth/token', form, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      const data = resp.data || {};

      if (data.mfa_setup_required) {
        // role exige 2FA e ainda não foi configurado — fluxo de setup obrigatório
        setMfaToken(data.mfa_token);
        setStep('mfa_setup');
        return;
      }
      if (data.mfa_required) {
        // 2FA já ativo nesta conta — falta validar o código
        setMfaToken(data.mfa_token);
        setStep('mfa_verify');
        return;
      }
      finishLogin(data.access_token);
    } catch (err) {
      const status = err.response?.status;
      setError(
        status === 401
          ? 'Credenciais inválidas.'
          : 'Ocorreu um erro. Tente mais tarde.',
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(email) {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await axiosAuth.post('/auth/recover-password', { email });
      setInfo('Se o email existir, foi enviado um link de recuperação.');
    } catch (err) {
      // o backend responde sempre 200 (anti-enumeração); um erro aqui é
      // rede/servidor indisponível.
      setError('Ocorreu um erro. Tente mais tarde.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-page">
      <div className="container">
        <div className="left"></div>
        <div className="right">
          <div className="login-card">
            <img src={Logo} className="logo" alt="Logo HotéisDigitais" />
            {step === 'login' && (
              <LoginForm
                onLogin={handleLogin}
                loading={loading}
                error={error}
                onForgot={handleForgot}
                info={info}
              />
            )}
            {step === 'mfa_setup' && (
              <TwoFactorSetup
                token={mfaToken}
                axiosAuth={axiosAuth}
                onDone={finishLogin}
                onCancel={() => setStep('login')}
              />
            )}
            {step === 'mfa_verify' && (
              <TwoFactorVerify
                mfaToken={mfaToken}
                axiosAuth={axiosAuth}
                onVerified={finishLogin}
                onCancel={() => setStep('login')}
              />
            )}
          </div>
        </div>
      </div>
      <div className="credits">
        <p>
          <a
            href="https://hoteisdigitais.com/"
            target="_blank"
            rel="noreferrer"
          >
            HoteisDigitais{' '}
          </a>
          @ {year}. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
